import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../utils/supabase.js';
import { getStreetAddressOnly } from '../../utils/addressParser';
import { generateDefaultMilestones } from '../../utils/milestoneUtils.js';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/ui/Icon';
import NotificationToast from '../../components/ui/NotificationToast';
import ShareLinkCard from '../../components/ShareLinkCard';
import PartiesSection from '../../components/PartiesSection';
import DocumentsSection from '../../components/DocumentsSection';
import UsefulLinks from '../../components/UsefulLinks';

const ContractSummaryPage = () => {
  console.log('🔥🔥🔥 ContractSummaryPage component loaded!');

  const { dealId } = useParams();
  console.log('🔥 dealId from URL:', dealId);

  const navigate = useNavigate();
  const [deal, setDeal] = useState(null);
  const [milestones, setMilestones] = useState([]);
  const [editedMilestones, setEditedMilestones] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [additionalTerms, setAdditionalTerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [isVerified, setIsVerified] = useState(false);
  const [selectedItems, setSelectedItems] = useState({
    milestones: {},
    deposits: {},
    terms: {}
  });
  const [partyInfo, setPartyInfo] = useState({
    buyer: null,
    seller: null,
    purchasePrice: null
  });
  const [representationRole, setRepresentationRole] = useState('buyer'); // 'buyer' or 'seller'
  const [clientType, setClientType] = useState(null); // 'buyer' or 'seller' - client identification
  const [parties, setParties] = useState([]);
  const [documents, setDocuments] = useState([]);


  useEffect(() => {
    loadDealData();
    // Save dealId to localStorage for header navigation
    if (dealId) {
      localStorage.setItem('lastDealId', dealId);
    }
  }, [dealId]);

  const loadDealData = async () => {
    console.log('🔥 ContractSummary - loadDealData called, dealId:', dealId);
    console.log('🔥 ContractSummary - Supabase available:', !!supabase);

    try {
      // Load deal and milestones
      const { data: dealData, error: dealError } = await supabase
        .from('deals')
        .select('*')
        .eq('id', dealId)
        .single();

      console.log('🔥 ContractSummary - Deal data from Supabase:', dealData);
      console.log('🔥 ContractSummary - Deal error:', dealError);

      if (dealError) throw dealError;

      const { data: milestonesData, error: milestonesError } = await supabase
        .from('milestones')
        .select('*')
        .eq('deal_id', dealId)
        .eq('status', 'active')
        .order('start_dt', { ascending: true });

      if (milestonesError) throw milestonesError;

      // Load contract data from localStorage to get deposits and additional terms
      let contractData = null;
      try {
        const contracts = JSON.parse(localStorage.getItem('contractParser_offlineContracts') || '[]');
        console.log('🔥 ContractSummary - All offline contracts:', contracts);
        console.log('🔥 ContractSummary - Looking for property_street:', dealData?.property_street);
        contractData = contracts.find(c => c.property_address?.includes(dealData.property_street));
        console.log('🔥 ContractSummary - Found matching contract:', contractData);
      } catch (e) {
        console.log('🔥 ContractSummary - Error loading offline contracts:', e);
      }

      // Parse deposits from contract data (handle both array and string formats)
      const depositsData = [];
      if (contractData?.extractedData?.financial?.deposits) {
        const deposits = contractData.extractedData.financial.deposits;

        // Handle array format (deposit_1, deposit_2, deposit_3)
        if (Array.isArray(deposits)) {
          deposits.forEach((deposit, index) => {
            if (deposit && deposit !== 'Not specified') {
              depositsData.push({
                id: `deposit-${index + 1}`,
                description: deposit,
                type: 'deposit'
              });
            }
          });
        }
        // Handle legacy string format
        else if (typeof deposits === 'string' && deposits !== 'Not specified') {
          depositsData.push({
            id: 'deposit-1',
            description: deposits,
            type: 'deposit'
          });
        }
      }

      console.log('🔥 ContractSummary - Deposits found:', depositsData);
      console.log('🔥 ContractSummary - Raw contract data:', contractData);
      console.log('🔥 ContractSummary - Buyer:', contractData?.extractedData?.parties?.buyer);
      console.log('🔥 ContractSummary - Seller:', contractData?.extractedData?.parties?.seller);
      console.log('🔥 ContractSummary - Purchase Price:', contractData?.extractedData?.property?.purchasePrice);

      // Parse additional terms from contract data
      const termsData = [];
      if (contractData?.extractedData?.nonStandardClauses) {
        contractData.extractedData.nonStandardClauses.forEach((term, index) => {
          termsData.push({
            id: `term-${index + 1}`,
            description: term,
            type: 'additional_term'
          });
        });
      }

      // Generate default milestones if closing date exists
      let allMilestones = [...milestonesData];
      const closingDate = contractData?.extractedData?.dates?.closingDate?.value || dealData?.closing_date;
      if (closingDate && dealId) {
        const defaultMilestones = generateDefaultMilestones(closingDate, dealId);
        // Add default milestones to the list
        allMilestones = [...allMilestones, ...defaultMilestones];
        console.log('🔥 ContractSummary - Generated default milestones:', defaultMilestones);
      }

      setDeal(dealData);
      setMilestones(allMilestones);
      setEditedMilestones([...allMilestones]);
      setDeposits(depositsData);
      setAdditionalTerms(termsData);

      // Set party information
      setPartyInfo({
        buyer: contractData?.extractedData?.parties?.buyer || null,
        seller: contractData?.extractedData?.parties?.seller || null,
        purchasePrice: contractData?.extractedData?.property?.purchasePrice || null
      });

      // Initialize selected items (all checked by default)
      const defaultSelected = {
        milestones: {},
        deposits: {},
        terms: {}
      };

      milestonesData.forEach(m => {
        defaultSelected.milestones[m.id] = true;
      });

      depositsData.forEach(d => {
        defaultSelected.deposits[d.id] = true;
      });

      termsData.forEach(t => {
        defaultSelected.terms[t.id] = true;
      });

      setSelectedItems(defaultSelected);

      // Auto-add uploaded contract file to documents section
      if (contractData?.contract_filename) {
        try {
          const storageKey = `contract_file_${contractData.contract_filename}`;
          const fileData = localStorage.getItem(storageKey);

          if (fileData) {
            console.log('🔥 ContractSummary - Found uploaded contract file:', contractData.contract_filename);
            const uploadedDocument = {
              id: `uploaded-contract-${dealId}`,
              name: contractData.contract_filename,
              size: contractData.file_size || 0,
              type: 'application/pdf',
              uploadedAt: contractData.created_at || new Date().toISOString(),
              url: fileData, // Base64 data URL from localStorage
              isOriginalContract: true // Mark this as the original uploaded contract
            };

            setDocuments([uploadedDocument]);
            console.log('🔥 ContractSummary - Auto-added contract file to documents:', uploadedDocument.name);
          }
        } catch (e) {
          console.warn('🔥 ContractSummary - Error loading contract file:', e);
        }
      }
      
    } catch (error) {
      console.error('Error loading deal data:', error);
      addNotification({
        type: 'error',
        title: 'Load Error',
        message: 'Failed to load contract data'
      });
    } finally {
      setLoading(false);
    }
  };

  const addNotification = (notification) => {
    const id = Date.now();
    setNotifications(prev => [...prev, { ...notification, id }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, notification.duration || 5000);
  };

  const handleMilestoneEdit = (index, field, value) => {
    const updated = [...editedMilestones];
    updated[index] = { ...updated[index], [field]: value };
    setEditedMilestones(updated);
  };

  const handleAddMilestone = () => {
    const newMilestone = {
      id: `new-${Date.now()}`,
      title: '',
      start_dt: '',
      end_dt: '',
      all_day: true,
      type: 'custom',
      status: 'active',
      deal_id: dealId
    };
    setEditedMilestones([...editedMilestones, newMilestone]);
    // Add to selected items by default
    setSelectedItems(prev => ({
      ...prev,
      milestones: { ...prev.milestones, [newMilestone.id]: true }
    }));
  };

  const handleItemSelection = (category, itemId, checked) => {
    setSelectedItems(prev => ({
      ...prev,
      [category]: { ...prev[category], [itemId]: checked }
    }));
  };

  const handleVerifyDates = async () => {
    setSaving(true);
    try {
      // Validate dates and required fields
      const errors = [];
      
      editedMilestones.forEach((milestone, index) => {
        if (!milestone.title?.trim()) {
          errors.push(`Milestone ${index + 1}: Title is required`);
        }
        if (!milestone.start_dt) {
          errors.push(`Milestone ${index + 1}: Date is required`);
        }
      });

      if (errors.length > 0) {
        addNotification({
          type: 'error',
          title: 'Validation Error',
          message: errors.join('; ')
        });
        setSaving(false);
        return;
      }

      // Separate new milestones from existing ones
      const newMilestones = [];
      const changedMilestones = [];
      
      editedMilestones.forEach((edited, index) => {
        // Check if this is a new milestone
        if (edited.id.toString().startsWith('new-')) {
          if (edited.title && edited.start_dt) {
            newMilestones.push({
              deal_id: dealId,
              title: edited.title,
              start_dt: edited.start_dt,
              end_dt: edited.start_dt,
              all_day: edited.all_day,
              type: edited.type || 'custom',
              status: 'active',
              sequence: 0,
              ics_uid: `milestone-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
              property_street: deal?.property_street,
              city: deal?.city,
              description: `Contract milestone: ${edited.title}`
            });
          }
        } else {
          // Check existing milestones for changes
          const original = milestones[index];
          if (original && JSON.stringify(edited) !== JSON.stringify(original)) {
            changedMilestones.push({
              ...edited,
              sequence: (original.sequence || 0) + 1
            });
          }
        }
      });

      // Insert new milestones
      if (newMilestones.length > 0) {
        const { error: insertError } = await supabase
          .from('milestones')
          .insert(newMilestones);
        
        if (insertError) throw insertError;
      }

      // Update changed existing milestones
      if (changedMilestones.length > 0) {
        const updatePromises = changedMilestones.map(milestone => 
          supabase
            .from('milestones')
            .update({
              title: milestone.title,
              start_dt: milestone.start_dt,
              end_dt: milestone.end_dt,
              all_day: milestone.all_day,
              sequence: milestone.sequence
            })
            .eq('id', milestone.id)
        );

        await Promise.all(updatePromises);
      }

      // Save draft snapshot with selected items and representation role
      const selectedData = {
        milestones: editedMilestones.filter(m => selectedItems.milestones[m.id]),
        deposits: deposits.filter(d => selectedItems.deposits[d.id]),
        additionalTerms: additionalTerms.filter(t => selectedItems.terms[t.id]),
        representationRole: representationRole,
        clientType: clientType, // Client identification (buyer or seller)
        partyInfo: partyInfo,
        parties: parties // Include additional parties in the snapshot
      };

      const { error: snapshotError } = await supabase
        .from('draft_snapshots')
        .insert({
          deal_id: dealId,
          selected_data: selectedData,
          created_at: new Date().toISOString()
        });

      if (snapshotError) throw snapshotError;

      setIsVerified(true);

      addNotification({
        type: 'success',
        title: 'Dates Confirmed',
        message: 'Your contract details have been saved successfully'
      });

    } catch (error) {
      console.error('Error verifying dates:', error);
      addNotification({
        type: 'error',
        title: 'Verification Error',
        message: 'Failed to verify dates'
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <Icon name="Loader" className="animate-spin mx-auto mb-4" size={48} />
            <p>Loading contract summary...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-20 pb-12">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2">
          {/* Page Header */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-3">
              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                <Icon name="FileCheck" size={24} color="var(--color-primary)" strokeWidth={2} />
              </div>
            </div>

            <h1 className="text-4xl font-bold text-white mb-3">
              Contract Summary
            </h1>

            <p className="text-xl text-gray-200 max-w-xl mx-auto leading-relaxed">
              {deal?.property_street && deal?.city ? `${deal.property_street}, ${deal.city}` : 'Contract Details'}
            </p>

            {/* Party Information */}
            {(partyInfo.buyer || partyInfo.seller || partyInfo.purchasePrice) && (
              <div className="mt-4 bg-white border border-gray-200 rounded-lg p-6 max-w-2xl mx-auto">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-left">
                  {partyInfo.buyer && (
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Buyer</p>
                      <p className="text-sm font-medium text-black">{partyInfo.buyer}</p>
                    </div>
                  )}
                  {partyInfo.seller && (
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Seller</p>
                      <p className="text-sm font-medium text-black">{partyInfo.seller}</p>
                    </div>
                  )}
                  {partyInfo.purchasePrice && (
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Purchase Price</p>
                      <p className="text-lg font-bold text-black">{partyInfo.purchasePrice}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Parties Section */}
          <PartiesSection
            buyerName={partyInfo.buyer}
            sellerName={partyInfo.seller}
            parties={parties}
            onUpdateParties={setParties}
            isReadOnly={false}
            clientType={clientType}
            onClientTypeChange={setClientType}
          />

          {/* Documents Section */}
          <DocumentsSection
            documents={documents}
            onUpdateDocuments={setDocuments}
            isReadOnly={false}
          />

          {/* Dates Section */}
          <div className="bg-white border border-gray-200 rounded-lg mb-6">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-black">Dates</h2>
                <button
                  onClick={handleAddMilestone}
                  className="bg-black text-white px-4 py-2 rounded hover:bg-gray-800 transition-colors focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2"
                >
                  Add New Date
                </button>
              </div>
            </div>
            
            <div className="">
              <div className="">
                {editedMilestones.map((milestone, index) => (
                  <div key={milestone.id} className="flex items-start space-x-4 p-4 border-b border-gray-200 last:border-b-0">
                    <input
                      type="checkbox"
                      checked={selectedItems.milestones[milestone.id] || false}
                      onChange={(e) => handleItemSelection('milestones', milestone.id, e.target.checked)}
                      className="w-5 h-5 mt-2 accent-black border-2 border-gray-300 rounded focus:ring-2 focus:ring-black"
                    />
                    <div className="flex-1">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                        <input
                          type="text"
                          placeholder="Milestone title"
                          value={milestone.title || ''}
                          onChange={(e) => handleMilestoneEdit(index, 'title', e.target.value)}
                          className="px-3 py-2 border border-gray-300 rounded bg-white text-black focus:ring-2 focus:ring-black focus:border-black"
                        />
                        <div className="flex items-center gap-2">
                          <input
                            type="datetime-local"
                            value={milestone.start_dt ? (() => {
                              const date = new Date(milestone.start_dt);
                              // Format for datetime-local input (YYYY-MM-DDTHH:MM)
                              const year = date.getFullYear();
                              const month = String(date.getMonth() + 1).padStart(2, '0');
                              const day = String(date.getDate()).padStart(2, '0');
                              const hours = String(date.getHours()).padStart(2, '0');
                              const minutes = String(date.getMinutes()).padStart(2, '0');
                              return `${year}-${month}-${day}T${hours}:${minutes}`;
                            })() : ''}
                            onChange={(e) => {
                              if (!e.target.value) return;
                              // Create date from local datetime input value
                              const inputDate = new Date(e.target.value);
                              handleMilestoneEdit(index, 'start_dt', inputDate.toISOString());

                              // Auto-uncheck "All Day" when time is set
                              if (milestone.all_day) {
                                handleMilestoneEdit(index, 'all_day', false);
                              }
                            }}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded bg-white text-black focus:ring-2 focus:ring-black focus:border-black"
                          />
                          {milestone.start_dt && (
                            <div className="relative group flex-shrink-0">
                              <button
                                onClick={() => {
                                  const streetAddress = getStreetAddressOnly(deal?.property_street) || 'Property';
                                  const eventTitle = `${streetAddress} - ${milestone.title}`;
                                  try {
                                    const eventDate = new Date(milestone.start_dt);
                                    const startDate = eventDate.toISOString().split('T')[0].replace(/-/g, '');
                                    const params = new URLSearchParams({
                                      action: 'TEMPLATE',
                                      text: eventTitle,
                                      dates: `${startDate}/${startDate}`,
                                      details: `Contract deadline: ${milestone.title}`,
                                      location: deal?.property_street ? `${deal.property_street}, ${deal.city}` : '',
                                      ctz: Intl.DateTimeFormat().resolvedOptions().timeZone
                                    });
                                    window.open(`https://calendar.google.com/calendar/render?${params.toString()}`, '_blank');
                                  } catch (error) {
                                    console.error('Error creating calendar link:', error);
                                    alert('Error creating calendar event');
                                  }
                                }}
                                className="p-2 bg-black text-white rounded hover:bg-gray-800 transition-colors focus:outline-none focus:ring-2 focus:ring-black"
                              >
                                <Icon name="Calendar" size={16} />
                              </button>
                              {/* Tooltip */}
                              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                                Add to Calendar
                                <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900"></div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      {milestone.description && milestone.title === 'Change Utilities' && (
                        <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-sm text-blue-800">
                          {milestone.description}
                        </div>
                      )}
                      <label className="flex items-center space-x-2 mt-2">
                        <input
                          type="checkbox"
                          checked={milestone.all_day || false}
                          onChange={(e) => {
                            handleMilestoneEdit(index, 'all_day', e.target.checked);

                            // If "All Day" is checked, set time to midnight (start of day)
                            if (e.target.checked && milestone.start_dt) {
                              const date = new Date(milestone.start_dt);
                              date.setHours(0, 0, 0, 0);
                              handleMilestoneEdit(index, 'start_dt', date.toISOString());
                            }
                          }}
                          className="w-4 h-4 accent-black border-gray-300 rounded focus:ring-2 focus:ring-black"
                        />
                        <span className="text-sm text-black">All Day</span>
                      </label>
                    </div>
                    {milestone.id.toString().startsWith('new-') && (
                      <button
                        onClick={() => {
                          setEditedMilestones(editedMilestones.filter((m, i) => i !== index));
                          setSelectedItems(prev => ({
                            ...prev,
                            milestones: { ...prev.milestones, [milestone.id]: undefined }
                          }));
                        }}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors flex-shrink-0"
                        title="Delete date"
                      >
                        <Icon name="Trash2" size={18} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Deposits Due Section */}
          {deposits.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-lg mb-6">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-2xl font-bold text-black">Deposits Due</h2>
              </div>
              
              <div className="">
                <div className="">
                  {deposits.map((deposit) => (
                    <div key={deposit.id} className="flex items-start space-x-4 p-4 border-b border-gray-200 last:border-b-0">
                      <input
                        type="checkbox"
                        checked={selectedItems.deposits[deposit.id] || false}
                        onChange={(e) => handleItemSelection('deposits', deposit.id, e.target.checked)}
                        className="w-5 h-5 mt-1 accent-black border-2 border-gray-300 rounded focus:ring-2 focus:ring-black"
                      />
                      <div className="flex-1">
                        <p className="text-black font-medium">{deposit.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Additional Terms Section */}
          {additionalTerms.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-lg mb-6">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-2xl font-bold text-black">Additional Terms</h2>
              </div>
              
              <div className="">
                <div className="">
                  {additionalTerms.map((term) => (
                    <div key={term.id} className="flex items-start space-x-4 p-4 border-b border-gray-200 last:border-b-0">
                      <input
                        type="checkbox"
                        checked={selectedItems.terms[term.id] || false}
                        onChange={(e) => handleItemSelection('terms', term.id, e.target.checked)}
                        className="w-5 h-5 mt-1 accent-black border-2 border-gray-300 rounded focus:ring-2 focus:ring-black"
                      />
                      <div className="flex-1">
                        <p className="text-black font-medium">{term.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Save and Share Buttons - Side by Side */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2 mb-6">
            <Button
              onClick={handleVerifyDates}
              disabled={saving}
              className="bg-black hover:bg-gray-800 text-white px-8 py-3 text-lg font-bold rounded"
            >
              {saving ? 'Saving...' : 'Save Contract Details'}
            </Button>

            <ShareLinkCard dealId={dealId} onShareLinkCreated={(link) => {
              addNotification({
                type: 'success',
                title: 'Link Created',
                message: 'Share link created successfully'
              });
            }} />
          </div>

          {/* Verification Confirmation */}
          {isVerified && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
              <div className="flex items-center mb-2">
                <Icon name="CheckCircle" size={20} color="green" className="mr-2" />
                <h3 className="text-green-800 font-semibold">Contract details saved successfully!</h3>
              </div>
              <p className="text-green-700 text-sm">Your contract information is ready to share. Use the link at the top to share with clients.</p>
            </div>
          )}

          {/* Calendar Actions */}
          <div className="mb-6">
            <div className="border border-gray-200 rounded-xl p-4 bg-black">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-white">Add to calendar</h3>
                <span className="text-xs text-gray-300">
                  {!isVerified ? 'Available before confirming dates' : 'Dates are still editable before you copy the client email.'}
                </span>
              </div>

              <div className="mt-3 flex gap-2">
                <a
                  href={`/e/${dealId}?mode=draft`}
                  target="_blank"
                  rel="noopener"
                  className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition-colors font-medium"
                >
                  Add All to Google Calendar
                </a>
                <a
                  href={`/e/${dealId}?mode=draft`}
                  className="bg-white text-black px-4 py-2 rounded hover:bg-gray-100 transition-colors font-medium"
                >
                  Download .ics (all)
                </a>
              </div>

              {/* Individual dates section */}
              <details className="mt-3">
                <summary className="cursor-pointer text-sm underline text-white">Add individual dates</summary>
                <ul className="mt-2 space-y-1">
                  {editedMilestones
                    .filter(m => selectedItems.milestones[m.id])
                    .map(m => (
                      <li key={m.id} className="flex items-center justify-between">
                        <span className="text-sm text-white">
                          {m.title} — {m.start_dt ? new Date(m.start_dt).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          }) : 'No date'}
                        </span>
                        <div className="flex gap-2">
                          <a
                            href={`/e/${dealId}?mode=draft&mid=${m.id}`}
                            target="_blank"
                            rel="noopener"
                            className="text-sm underline text-blue-300 hover:text-blue-100"
                          >
                            Google
                          </a>
                          <a
                            href={`/e/${dealId}?mode=draft&mid=${m.id}`}
                            className="text-sm underline text-white hover:text-gray-300"
                          >
                            .ics
                          </a>
                        </div>
                      </li>
                    ))
                  }
                </ul>
              </details>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex justify-start">
            <Button
              variant="outline"
              onClick={() => navigate('/')}
              className="border-black text-black hover:bg-gray-100 font-medium"
            >
              Back to Upload
            </Button>
          </div>
          </div>

          {/* Sidebar - Hidden on Mobile, Visible on Desktop */}
          <div className="hidden lg:block">
            <UsefulLinks
              propertyCity={deal?.city || 'Your Location'}
              isReadOnly={false}
              isMobile={false}
              dealId={dealId}
            />
          </div>
        </div>
      </main>

      {/* Mobile Useful Links - Collapsible */}
      <div className="lg:hidden pt-6 pb-6 px-6 max-w-7xl mx-auto">
        <UsefulLinks
          propertyCity={deal?.city || 'Your Location'}
          isReadOnly={false}
          isMobile={true}
          dealId={dealId}
        />
      </div>

      {/* Notifications */}
      {notifications.length > 0 && (
        <NotificationToast
          notifications={notifications}
          onDismiss={(id) => setNotifications(prev => prev.filter(n => n.id !== id))}
          maxVisible={3}
        />
      )}
    </div>
  );
};

export default ContractSummaryPage;